import { useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ErrorPageDetails } from "../../components/ErrorPageDetails";
import { Loading } from "../../components/Loading";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import DetailsLabel from "../../components/DetailsLabel";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import {
  getMatch,
  getMatchEvents,
  updateMatch,
  deleteMatch,
} from "../../services/match.service";
import {
  fetchWorldCupNationMap,
  resolveCountryMatchDisplay,
} from "../../utils/worldCupNation";
import {
  createMatchEvent,
  updateMatchEvent,
  deleteMatchEvent,
} from "../../services/matchEvent.service";
import { TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditMatchForm from "./EditMatchForm";
import MatchEventForm from "./MatchEventForm";
import MatchEventsTable from "./MatchEventsTable";
import {
  DATE_TIME_FORMAT,
  MATCH_EVENTS_PER_PAGE,
  MatchType,
} from "../../utils/constants";

const InnerContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
});
const RowContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginBlock: "16px",
  padding: 0,
});
const ImageContainer = styled(Box)({
  position: "relative",
  width: "60px",
  height: "60px",
  marginRight: "16px",
});
const Image = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const MatchView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventSearch, setEventSearch] = useState("");
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const lastAutoLoadedEventsKeyRef = useRef(null);

  const {
    data: match,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["match", id],
    queryFn: () => getMatch(id),
  });

  const { data: worldCupNationMap } = useQuery({
    queryKey: ["worldCupNationMap"],
    queryFn: fetchWorldCupNationMap,
    staleTime: 5 * 60 * 1000,
  });

  const loadEvents = async (reset = false) => {
    setEventsLoading(true);
    try {
      const lastId = reset ? undefined : events[events.length - 1]?.id;
      const newEvents = await getMatchEvents(id, {
        perPage: MATCH_EVENTS_PER_PAGE,
        lastId,
        search: eventSearch,
      });
      setEvents(reset ? newEvents : [...events, ...newEvents]);
    } catch (error) {
      alert(`Error loading events: ${error}`);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (match) {
      const key = `${id}:${eventSearch}`;
      if (lastAutoLoadedEventsKeyRef.current === key) {
        return;
      }
      lastAutoLoadedEventsKeyRef.current = key;
      loadEvents(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match, eventSearch, id]);

  const { mutate: updateMatchMutation, isPending: isUpdatePending } =
    useMutation({
      mutationFn: (payload) => updateMatch(id, payload),
      onSuccess: () => {
        alert("Match updated successfully");
        setIsEditing(false);
        refetch();
      },
      onError: (error) => {
        alert(`Error updating match: ${error}`);
      },
    });

  const handleSaveMatch = (payload) => {
    updateMatchMutation(payload);
  };

  const { mutate: deleteMatchMutation, isPending: isDeletePending } =
    useMutation({
      mutationFn: () => deleteMatch(id),
      onSuccess: () => {
        alert("Match deleted successfully");
        navigate("/matches");
      },
      onError: (error) => {
        alert(`Error deleting match: ${error}`);
      },
    });

  const { mutate: createEventMutation, isPending: isCreateEventPending } =
    useMutation({
      mutationFn: (payload) => createMatchEvent(id, payload),
      onSuccess: () => {
        alert("Event created successfully");
        setShowAddEvent(false);
        loadEvents(true);
      },
      onError: (error) => {
        alert(`Error creating event: ${error}`);
      },
    });

  const { mutate: updateEventMutation, isPending: isUpdateEventPending } =
    useMutation({
      mutationFn: ({ id, payload }) => updateMatchEvent(id, payload),
      onSuccess: () => {
        alert("Event updated successfully");
        setEditingEvent(null);
        loadEvents(true);
      },
      onError: (error) => {
        alert(`Error updating event: ${error}`);
      },
    });

  const { mutate: deleteEventMutation } = useMutation({
    mutationFn: (eventId) => deleteMatchEvent(eventId),
    onSuccess: () => {
      alert("Event deleted successfully");
      loadEvents(true);
    },
    onError: (error) => {
      alert(`Error deleting event: ${error}`);
    },
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDeleteMatch = () => {
    if (
      window.confirm(
        "Are you sure you want to delete this match? This action cannot be undone.",
      )
    ) {
      deleteMatchMutation();
    }
  };

  const handleAddEventClick = () => {
    setShowAddEvent(true);
    setEditingEvent(null);
  };

  const handleEditEventClick = (event) => {
    setShowAddEvent(false);
    setEditingEvent(event);
  };

  const handleCancelEventForm = () => {
    setShowAddEvent(false);
    setEditingEvent(null);
  };

  const handleSubmitEvent = (payload) => {
    if (editingEvent) {
      updateEventMutation({ id: editingEvent.id, payload });
    } else {
      createEventMutation(payload);
    }
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      deleteEventMutation(eventId);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorPageDetails text={"Error loading match"} />;
  }

  const isCountryMatch =
    match?.type === MatchType.COUNTRIES ||
    !!(match?.homeCountry || match?.awayCountry);

  const leagueName = match?.league?.name;

  const homeCountryName = match?.homeCountry
    ? resolveCountryMatchDisplay(
        match.homeCountry,
        leagueName,
        worldCupNationMap,
      )
    : null;

  const awayCountryName = match?.awayCountry
    ? resolveCountryMatchDisplay(
        match.awayCountry,
        leagueName,
        worldCupNationMap,
      )
    : null;

  return (
    <Container component="main" maxWidth="lg">
      <InnerContainer>
        <RowContainer>
          <Box sx={{ flex: 1 }}>
            <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
              Match Details
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {!isCountryMatch && match.homeTeam?.logo && (
                <ImageContainer>
                  <Image src={match.homeTeam.logo} alt="Home team logo" />
                </ImageContainer>
              )}

              <Typography variant="h5">
                {isCountryMatch
                  ? homeCountryName || "N/A"
                  : match.homeTeam?.name || `Team ${match.homeTeamId}`}
              </Typography>

              <Typography variant="h5" sx={{ mx: 2 }}>
                vs
              </Typography>

              {!isCountryMatch && match.awayTeam?.logo && (
                <ImageContainer>
                  <Image src={match.awayTeam.logo} alt="Away team logo" />
                </ImageContainer>
              )}

              <Typography variant="h5">
                {isCountryMatch
                  ? awayCountryName || "N/A"
                  : match.awayTeam?.name || `Team ${match.awayTeamId}`}
              </Typography>
            </Box>

            {isEditing ? (
              <EditMatchForm
                match={match}
                onSave={handleSaveMatch}
                onCancel={handleCancelEdit}
                isUpdatePending={isUpdatePending}
              />
            ) : (
              <>
                <DetailsLabel
                  label="League"
                  value={match.league?.name || `League ${match.leagueId}`}
                />

                <DetailsLabel label="Round" value={match.round || "N/A"} />

                <DetailsLabel label="Status" value={match.status} />

                {isCountryMatch && (
                  <>
                    <DetailsLabel
                      label="Home Country"
                      value={homeCountryName || "N/A"}
                    />

                    <DetailsLabel
                      label="Away Country"
                      value={awayCountryName || "N/A"}
                    />
                  </>
                )}

                <DetailsLabel
                  label={isCountryMatch ? "Home Score" : "Home Team Score"}
                  value={match.homeTeamScore ?? "N/A"}
                />

                <DetailsLabel
                  label={isCountryMatch ? "Away Score" : "Away Team Score"}
                  value={match.awayTeamScore ?? "N/A"}
                />

                <DetailsLabel
                  label="Start At"
                  value={
                    match.startAt
                      ? format(new Date(match.startAt), DATE_TIME_FORMAT)
                      : "N/A"
                  }
                />

                <DetailsLabel
                  label="Created At"
                  value={
                    match.createdAt
                      ? format(new Date(match.createdAt), DATE_TIME_FORMAT)
                      : "N/A"
                  }
                />

                <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                  >
                    Edit Match
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteMatch}
                    disabled={isDeletePending}
                  >
                    Delete Match
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </RowContainer>

        <RowContainer>
          <Box sx={{ width: "100%" }}>
            <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
              Match Events
            </Typography>

            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label="Search by player name"
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                size="small"
                sx={{ flexGrow: 1 }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEventClick}
                disabled={!!(showAddEvent || editingEvent)}
              >
                Add Event
              </Button>
            </Box>

            {(showAddEvent || editingEvent) && (
              <MatchEventForm
                matchId={id}
                homeTeam={{
                  id: match.homeTeamId,
                  name: match.homeTeam?.name,
                }}
                awayTeam={{
                  id: match.awayTeamId,
                  name: match.awayTeam?.name,
                }}
                isCountryMatch={isCountryMatch}
                homeSideLabel={isCountryMatch ? homeCountryName : undefined}
                awaySideLabel={isCountryMatch ? awayCountryName : undefined}
                homeCountryCode={
                  isCountryMatch && match.homeCountry
                    ? String(match.homeCountry).toUpperCase()
                    : undefined
                }
                awayCountryCode={
                  isCountryMatch && match.awayCountry
                    ? String(match.awayCountry).toUpperCase()
                    : undefined
                }
                editingEvent={editingEvent}
                onSubmit={handleSubmitEvent}
                onCancel={handleCancelEventForm}
                isSubmitting={isCreateEventPending || isUpdateEventPending}
              />
            )}

            <MatchEventsTable
              events={events}
              eventsLoading={eventsLoading}
              onEditEvent={handleEditEventClick}
              onDeleteEvent={handleDeleteEvent}
              onLoadMore={() => loadEvents(false)}
              showAddEvent={showAddEvent}
              editingEvent={editingEvent}
              isCountryMatch={isCountryMatch}
              homeSide={{
                id: match.homeTeamId,
                code:
                  isCountryMatch && match.homeCountry
                    ? String(match.homeCountry).toUpperCase()
                    : undefined,
                label: homeCountryName || "N/A",
              }}
              awaySide={{
                id: match.awayTeamId,
                code:
                  isCountryMatch && match.awayCountry
                    ? String(match.awayCountry).toUpperCase()
                    : undefined,
                label: awayCountryName || "N/A",
              }}
              nationMap={worldCupNationMap}
            />
          </Box>
        </RowContainer>
      </InnerContainer>
    </Container>
  );
};

export default MatchView;
